import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, User, Smartphone, KeyRound, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo viajes.png";

const SUBTITLES = {
  credentials: 'Inicia sesión para gestionar tu web',
  setup: 'Configura tu app Authenticator para proteger tu cuenta',
  verify: 'Introduce el código de tu app Authenticator',
  recovery: 'Guarda tus códigos de recuperación',
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // credentials -> (setup | verify) -> [recovery] -> dashboard
  const [step, setStep] = useState('credentials');
  const [mfa, setMfa] = useState(null); // { mfa_token, qr_code?, secret? }
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  useEffect(() => {
    // Si ya hay token y usuario, redirigir al dashboard
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      navigate('/gestion-agencia/dashboard');
    }
  }, [navigate]);

  const startSession = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
  };

  const backToLogin = () => {
    setStep('credentials');
    setMfa(null);
    setMfaCode('');
    setUseRecoveryCode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (step === 'setup' || step === 'verify') {
        const res = await axios.post(`${API}/auth/verify-2fa`, {
          mfa_token: mfa.mfa_token,
          code: mfaCode
        });

        if (res.data.recovery_codes) {
          // Alta completada: mostrar los códigos antes de entrar
          setRecoveryCodes(res.data.recovery_codes);
          startSession(res.data);
          setStep('recovery');
          return;
        }

        startSession(res.data);
        if (res.data.recovery_codes_remaining !== undefined) {
          toast.warning(`Has usado un código de recuperación. Te quedan ${res.data.recovery_codes_remaining}.`);
        } else {
          toast.success('¡Autenticación correcta!');
        }
        navigate('/gestion-agencia/dashboard');
        return;
      }

      const res = await axios.post(`${API}/auth/login`, credentials);

      if (res.data.mfa_setup_required) {
        setMfa(res.data);
        setStep('setup');
      } else if (res.data.mfa_required) {
        setMfa(res.data);
        setStep('verify');
      } else {
        startSession(res.data);
        toast.success('¡Bienvenido!');
        navigate('/gestion-agencia/dashboard');
      }
    } catch (err) {
      console.error('Error en login:', err);
      const detail = err.response?.data?.detail || 'Error en la autenticación';
      toast.error(detail);
      // Un código erróneo permite reintentar; si el desafío ha caducado o se ha
      // bloqueado, hay que volver a introducir la contraseña
      const challengeExpired = err.response?.status !== 401 || /Vuelve a iniciar sesión/.test(detail);
      if (step !== 'credentials' && challengeExpired) {
        backToLogin();
      }
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      toast.success('Códigos copiados');
    } catch {
      toast.error('No se pudieron copiar. Anótalos manualmente.');
    }
  };

  const codeInput = (
    <div className="form-group">
      <label className="form-label flex items-center gap-2 text-primary font-bold">
        {useRecoveryCode ? <KeyRound className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
        {useRecoveryCode ? 'Código de recuperación' : 'Código de 6 dígitos'}
      </label>
      {useRecoveryCode ? (
        <Input
          required
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value)}
          placeholder="XXXXX-XXXXX"
          maxLength={11}
          className="form-input text-center text-xl tracking-[4px] font-mono uppercase"
          data-testid="admin-mfa-code"
          autoFocus
        />
      ) : (
        <Input
          required
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          className="form-input text-center text-2xl tracking-[10px] font-mono"
          data-testid="admin-mfa-code"
          autoFocus
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Viajes de Ensueño" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl font-serif font-semibold text-primary">
            Panel de Administración
          </h1>
          <p className="text-muted-foreground mt-2">{SUBTITLES[step]}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="admin-login-form">
          {step === 'recovery' ? (
            <div className="space-y-6">
              <p className="text-sm text-muted-foreground">
                Si pierdes el móvil podrás entrar con uno de estos códigos. Cada código sirve <strong>una sola vez</strong>.
                Guárdalos en un lugar seguro: <strong>no se volverán a mostrar</strong>.
              </p>
              <ul className="grid grid-cols-2 gap-2 font-mono text-center" data-testid="admin-recovery-codes">
                {recoveryCodes.map((code) => (
                  <li key={code} className="bg-slate-100 rounded py-2">{code}</li>
                ))}
              </ul>
              <Button type="button" variant="outline" className="w-full" onClick={copyRecoveryCodes}>
                <Copy className="w-4 h-4 mr-2" />
                Copiar códigos
              </Button>
              <Button
                type="button"
                className="w-full btn-primary py-6"
                onClick={() => navigate('/gestion-agencia/dashboard')}
              >
                Ya los he guardado, continuar
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 'credentials' && (
                <>
                  <div className="form-group">
                    <label className="form-label flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Usuario
                    </label>
                    <Input
                      required
                      value={credentials.username}
                      onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                      placeholder="Tu usuario"
                      data-testid="admin-username"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Contraseña
                    </label>
                    <Input
                      type="password"
                      required
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                      placeholder="Tu contraseña"
                      data-testid="admin-password"
                      className="form-input"
                    />
                  </div>
                </>
              )}

              {step === 'setup' && (
                <>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Abre Google Authenticator, Microsoft Authenticator o similar.</li>
                    <li>Escanea este código QR.</li>
                    <li>Introduce el código de 6 dígitos que te muestra la app.</li>
                  </ol>
                  <img
                    src={mfa.qr_code}
                    alt="Código QR para configurar el Authenticator"
                    className="mx-auto w-48 h-48"
                    data-testid="admin-mfa-qr"
                  />
                  <div className="text-center text-xs text-muted-foreground">
                    <Smartphone className="w-4 h-4 inline mr-1" />
                    ¿No puedes escanearlo? Introduce esta clave:
                    <code className="block mt-1 font-mono text-sm text-foreground break-all select-all">
                      {mfa.secret}
                    </code>
                  </div>
                  {codeInput}
                </>
              )}

              {step === 'verify' && (
                <>
                  {codeInput}
                  <button
                    type="button"
                    onClick={() => { setUseRecoveryCode(!useRecoveryCode); setMfaCode(''); }}
                    className="text-xs text-primary hover:underline"
                  >
                    {useRecoveryCode ? 'Usar el código de la app' : '¿Has perdido el móvil? Usa un código de recuperación'}
                  </button>
                </>
              )}

              {step !== 'credentials' && (
                <button
                  type="button"
                  onClick={backToLogin}
                  className="block text-xs text-primary hover:underline"
                >
                  Volver al login
                </button>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full btn-primary py-6"
              >
                {loading ? 'Procesando...' : (
                  step === 'credentials' ? 'Iniciar Sesión' : (step === 'setup' ? 'Activar Authenticator' : 'Verificar Código')
                )}
              </Button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          ¿Problemas para acceder?{' '}
          <a href="mailto:soporte@viajesensueno.com" className="text-primary hover:underline">
            Contacta con soporte
          </a>
        </p>
      </div>
    </div>
  );
}
